import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotifications } from './NotificationsProvider';
import { preferenceOwner, saveNotificationPreference } from './preferenceWrites';

export default function useNotificationToggle({ active, draft, inAppDraft, preferences,
  setDraft, setInAppDraft, setPreferences, setFeedback, generation }) {
  const { enablePush, deviceStatus } = useNotifications();
  const [waiting, setWaiting] = useState({});
  const intents = useRef({}), confirmed = useRef({}), permission = useRef(null);
  const mounted = useRef(false), current = useRef(null);
  current.current = { active, notifications: draft, in_app: inAppDraft, preferences };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const owns = useCallback(intent => intent.owner === preferenceOwner()
    && (!mounted.current || intent.generation === generation.current), [generation]);
  const latest = useCallback(intent => owns(intent) && intents.current[intent.id] === intent, [owns]);
  const update = useCallback((intent, value) => {
    if (!mounted.current || !latest(intent)) return;
    current.current[intent.group] = { ...current.current[intent.group], [intent.key]: value };
    (intent.group === 'notifications' ? setDraft : setInAppDraft)(previous => ({ ...previous, [intent.key]: value }));
  }, [latest, setDraft, setInAppDraft]);
  const rollback = useCallback(intent => {
    intent.reverted = true;
    update(intent, confirmed.current[intent.id] ?? current.current.preferences?.[intent.group]?.[intent.key] ?? false);
  }, [update]);
  const save = useCallback(async intent => {
    try {
      const result = await saveNotificationPreference(intent.group, intent.key, intent.value, intent.owner);
      if (!owns(intent)) return;
      const value = result.preferences?.[intent.group]?.[intent.key] ?? intent.value;
      confirmed.current[intent.id] = value;
      if (mounted.current) setPreferences(previous => ({ ...previous,
        [intent.group]: { ...previous?.[intent.group], [intent.key]: value } }));
      update(intent, value);
      const newest = intents.current[intent.id];
      if (newest?.reverted) rollback(newest);
    } catch (error) {
      if (mounted.current && latest(intent) && error.message !== 'PUSH_SESSION_CHANGED') {
        rollback(intent); setFeedback('brand:saveError');
      }
    }
  }, [owns, latest, update, rollback, setPreferences, setFeedback]);

  // Returning from OS Settings completes only still-current explicit choices.
  useEffect(() => {
    if (deviceStatus?.permission !== 'granted' || deviceStatus?.registration !== 'registered') return;
    const pending = Object.values(waiting);
    if (!pending.length) return;
    setWaiting({});
    pending.forEach(intent => { if (latest(intent)) { intent.reverted = false; update(intent, true); void save(intent); } });
  }, [deviceStatus?.permission, deviceStatus?.registration, waiting, latest, update, save]);

  const toggle = async (group, key) => {
    if (!current.current.active) return;
    const id = `${group}.${key}`;
    const intent = { id, group, key, value: !current.current[group]?.[key], owner: preferenceOwner(),
      generation: generation.current };
    intents.current[id] = intent;
    setWaiting(previous => { const next = { ...previous }; delete next[id]; return next; });
    setFeedback('');
    update(intent, intent.value);
    if (group === 'notifications' && intent.value) {
      try {
        // Share one native permission/registration request across quick taps.
        if (!permission.current) {
          const request = Promise.resolve(enablePush());
          permission.current = request;
          const clear = () => { if (permission.current === request) permission.current = null; };
          request.then(clear, clear);
        }
        const result = await permission.current;
        if (!latest(intent)) return;
        if (result?.settingsOpened) {
          if (!mounted.current) return;
          rollback(intent);
          setWaiting(previous => ({ ...previous, [id]: intent }));
          return;
        }
      } catch (error) {
        if (mounted.current && latest(intent)) {
          rollback(intent);
          if (!['PUSH_PERMISSION_CANCELLED', 'PUSH_SESSION_CHANGED'].includes(error.message)) {
            setFeedback(`notifications:${error.message === 'PUSH_PERMISSION_DENIED' ? 'permissionDenied' : 'registrationFailed'}`);
          }
        }
        return;
      }
    }
    if (latest(intent)) await save(intent);
  };
  return { toggleNotification: key => toggle('notifications', key), toggleInApp: key => toggle('in_app', key) };
}
